import { SparkleIcon } from '@phosphor-icons/react';
import type { AgentChatSummary } from '@regia/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router';
import { http } from '../../lib/api';
import { formatAgo } from '../../lib/format';
import { cn, EmptyState, PageHeader, Skeleton } from '../../ui';
import { PageBody } from '../common';
import { AiNotReady } from '../insights';

export function useAgentChats() {
  return useQuery({ queryKey: ['agent-chats'], queryFn: () => http.get<AgentChatSummary[]>('/api/agents/chats'), refetchInterval: 30_000 });
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => w.length > 2 || /^[A-Z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || name.slice(0, 2).toUpperCase();

export function AgentAvatar({ name, color, fg, size = 48, badge }: { name: string; color: string; fg: string; size?: number; badge?: number }) {
  return (
    <span className="relative inline-flex shrink-0">
      <span
        className="t-title flex items-center justify-center rounded-full"
        style={{ width: size, height: size, backgroundColor: color, color: fg, fontSize: size * 0.36 }}
        aria-hidden
      >
        {initials(name)}
      </span>
      {badge ? (
        <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-alert px-1 text-xs font-bold text-white ring-2 ring-paper">
          {badge}
        </span>
      ) : null}
    </span>
  );
}

/** In cima a Oggi: gli agenti, uno per scheda, a un tocco dalla loro chat. */
export function AgentsStrip() {
  const chats = useAgentChats();
  if (!chats.data?.length) return null;
  return (
    <div className="mb-6 -mx-4 lg:mx-0">
      <div className="no-scrollbar flex gap-4 overflow-x-auto px-4 pb-1 lg:px-0">
        <Link to="/ai" className="flex w-16 shrink-0 flex-col items-center gap-1.5">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-sheet">
            <SparkleIcon size={26} weight="fill" aria-hidden />
          </span>
          <span className="w-full truncate text-center text-xs font-medium text-ink">Tutte</span>
        </Link>
        {chats.data.map((c) => (
          <Link key={c.pageId} to={`/schede/${c.pageSlug}/ai`} className="flex w-16 shrink-0 flex-col items-center gap-1.5">
            <AgentAvatar name={c.pageName} color={c.color} fg={c.colorFg} size={56} badge={c.pending} />
            <span className="w-full truncate text-center text-xs font-medium text-ink">{c.pageName}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/** Chat: un agente per ogni scheda, come i contatti nei messaggi del telefono. */
export function ChatListPage() {
  const chats = useAgentChats();
  return (
    <PageBody className="mx-auto max-w-2xl">
      <PageHeader title="Chat" subtitle="Un agente per ogni scheda: lavora solo per quel cliente e si ricorda tutto." />
      <AiNotReady />
      <ul className="overflow-hidden rounded-[var(--radius-sheet)] bg-sheet">
        <li className="border-b border-line-2">
          <Link to="/ai" className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-sheet-2">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ink text-sheet">
              <SparkleIcon size={24} weight="fill" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[0.9375rem] font-semibold text-ink">Regia</span>
              <span className="block truncate text-sm text-ink-2">Domande su tutte le schede insieme</span>
            </span>
          </Link>
        </li>
        {chats.isLoading ? (
          <li className="p-4">
            <Skeleton className="h-40 w-full" />
          </li>
        ) : (
          chats.data?.map((c) => (
            <li key={c.pageId} className="border-b border-line-2 last:border-b-0">
              <Link to={`/schede/${c.pageSlug}/ai`} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-sheet-2">
                <AgentAvatar name={c.pageName} color={c.color} fg={c.colorFg} badge={c.pending} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold text-ink">{c.pageName}</span>
                    {c.lastAt ? <span className="shrink-0 text-xs text-ink-3">{formatAgo(c.lastAt)}</span> : null}
                  </span>
                  <span className={cn('block truncate text-sm', c.lastText ? 'text-ink-2' : 'text-ink-3')}>
                    {c.lastText ? `${c.lastFromAgent ? '' : 'Tu: '}${c.lastText}` : `Scrivi a ${c.agentName}`}
                  </span>
                </span>
              </Link>
            </li>
          ))
        )}
      </ul>
      {!chats.isLoading && !chats.data?.length ? (
        <EmptyState icon={SparkleIcon} title="Nessuna scheda" message="Crea una scheda: ogni scheda ha il suo agente con cui chattare." className="mt-6" />
      ) : null}
    </PageBody>
  );
}

/** I vecchi indirizzi /chat/<scheda> portano alla sezione Agente della scheda, dove c'è la chat. */
export function AgentChatPage() {
  const { slug = '' } = useParams();
  return <Navigate to={`/schede/${slug}/ai`} replace />;
}
